import type { RailItemId } from "@/shell/ide-v2/types";

const STUDIO_CAPABILITY_OPERATION_DISPATCHER =
  "cogo.studio.capability.executeOperation";

export const STUDIO_MODULE_IDS = [
  "application-preview",
  "figma",
  "page-editor",
  "actionflow",
  "data-source",
  "document",
  "report",
  "cogo-json",
  "release",
] as const;

export type StudioModuleId = (typeof STUDIO_MODULE_IDS)[number];

export interface StudioModuleContract {
  id: StudioModuleId;
  label: string;
  owner: "studio";
  primarySurface: RailItemId;
  supportingSurfaces: readonly RailItemId[];
  uiStatus: "integrated" | "partial" | "planned";
  responsibilities: readonly string[];
  adapters: readonly string[];
  ontologyBoundary: string;
}

export const STUDIO_MODULE_CONTRACTS: Record<
  StudioModuleId,
  StudioModuleContract
> = {
  "application-preview": {
    id: "application-preview",
    label: "Application preview",
    owner: "studio",
    primarySurface: "preview",
    supportingSurfaces: ["evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Render and interact with Coogo JSON applications",
      "Switch pages, viewports, and runtime states",
      "Expose creator-safe runtime checks and developer evidence",
    ],
    adapters: ["Coogo React renderer", "runtime-v2"],
    ontologyBoundary:
      "Consumes validated ontology outputs; application preview remains a Studio runtime responsibility.",
  },
  figma: {
    id: "figma",
    label: "Figma workspace",
    owner: "studio",
    primarySurface: "source",
    supportingSurfaces: ["preview", "evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Import, inspect, revise, version, and export .fig sources",
      "Present source tree, page/story mapping, and token decisions",
      "Read the design source and run bounded visual judgement",
    ],
    adapters: ["design source reader", "Gemma visual judgement"],
    ontologyBoundary:
      "Studio owns the source workspace; ontology owns extraction, semantic validation, and promotion decisions.",
  },
  "page-editor": {
    id: "page-editor",
    label: "Page editor",
    owner: "studio",
    primarySurface: "editor",
    supportingSurfaces: ["preview"],
    uiStatus: "integrated",
    responsibilities: [
      "Create, select, order, and edit application pages",
      "Edit blocks, elements, layout, metadata, and routes",
      "Keep direct manipulation and chat operations equivalent",
    ],
    adapters: ["Coogo page model", "Studio MCP"],
    ontologyBoundary:
      "Ontology may provide page candidates and constraints but never owns page editing state.",
  },
  actionflow: {
    id: "actionflow",
    label: "ActionFlow editor",
    owner: "studio",
    primarySurface: "behavior",
    supportingSurfaces: ["preview", "evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Inspect and edit ActionFlow steps and event bindings",
      "Run flow validation and runtime tests",
      "Provide selection-aware chat operations with confirmation",
    ],
    adapters: ["ActionFlow editor", "runtime-v2", "Studio MCP"],
    ontologyBoundary:
      "Ontology may describe behavior intent; executable ActionFlow authoring and testing remain in Studio.",
  },
  "data-source": {
    id: "data-source",
    label: "Data source editor",
    owner: "studio",
    primarySurface: "data",
    supportingSurfaces: ["preview", "evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Create and configure data sources, variables, and bindings",
      "Test connections and inspect the bounded result",
      "Debug data-to-component and data-to-flow relationships",
    ],
    adapters: ["Coogo datasource schema", "Studio MCP"],
    ontologyBoundary:
      "Ontology may supply semantic data candidates; credentials, binding, execution, and debugging belong to Studio.",
  },
  document: {
    id: "document",
    label: "Document workspace",
    owner: "studio",
    primarySurface: "document",
    supportingSurfaces: ["report", "evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Edit canonical document pages and nodes",
      "Coordinate proposal-first agent collaboration",
      "Export and verify DOCX, HWPX, PPTX, PDF, and XLSX targets",
    ],
    adapters: ["WordGard collaboration adapter", "native document exporters"],
    ontologyBoundary:
      "Ontology may contribute document semantics; Studio owns document state, proposals, native export, and export checks.",
  },
  report: {
    id: "report",
    label: "Report workspace",
    owner: "studio",
    primarySurface: "report",
    supportingSurfaces: ["document", "evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Compose report pages and sections",
      "Edit report semantics and preview native targets",
      "Separate creator workflow from developer proof detail",
    ],
    adapters: ["document workspace", "native report proof"],
    ontologyBoundary:
      "Ontology may validate report meaning; report authoring, preview, and export remain Studio modules.",
  },
  "cogo-json": {
    id: "cogo-json",
    label: "Coogo JSON inspector",
    owner: "studio",
    primarySurface: "code",
    supportingSurfaces: ["map", "evidence"],
    uiStatus: "integrated",
    responsibilities: [
      "Inspect, import, export, and validate Coogo JSON bundles",
      "Show schema, runtime, and source-authority evidence in developer mode",
      "Navigate from JSON entities to editable Studio surfaces",
    ],
    adapters: ["Coogo schema v4", "Coogo validators"],
    ontologyBoundary:
      "Ontology-specific graph rendering belongs to ontology; Coogo JSON application inspection and rendering belong to Studio.",
  },
  release: {
    id: "release",
    label: "Release",
    owner: "studio",
    primarySurface: "publish",
    supportingSurfaces: ["deploy", "evidence"],
    uiStatus: "partial",
    responsibilities: [
      "Validate release readiness",
      "Publish and deploy with explicit confirmation",
      "Retain release checks and rollback evidence",
    ],
    adapters: ["Studio publish service", "deployment providers"],
    ontologyBoundary:
      "Ontology evidence may block release, but publishing and deployment are Studio responsibilities.",
  },
};

export const ONTOLOGY_OWNERSHIP_CONTRACT = {
  owner: "ontology",
  responsibilities: [
    "Design detail and semantic candidate extraction",
    "Ontology validation, gap detection, and promotion",
    "Ontology graph and ontology-specific evidence rendering",
    "Validated design-detail, readiness, and evidence bundle publication",
  ],
  outputsConsumedByStudio: [
    "design details",
    "ontology candidates",
    "readiness and blocker evidence",
    "ontology visualization results",
  ],
  excludedStudioResponsibilities: [
    "Figma import, viewing, editing, revision, and export",
    "Coogo JSON application preview and inspection",
    "Page, ActionFlow, and data-source editing",
    "Document, WordGard, report, and native export workflows",
    "Application publishing and deployment",
  ],
} as const;

export type StudioDocumentTarget = "docx" | "hwpx" | "pptx" | "pdf" | "xlsx";
export type StudioRevision = string | number;

export type StudioTargetRef =
  | { kind: "app"; projectId: string }
  | { kind: "page"; pageId: string; revision?: StudioRevision }
  | { kind: "element"; pageId: string; blockName: string; elementId: string }
  | { kind: "flow"; flowId: string; blockName?: string }
  | { kind: "datasource"; blockName: string; sourceName: string }
  | { kind: "data-binding"; blockName: string; elementId: string }
  | {
      kind: "figma-source";
      workspaceId: string;
      sourceRevision?: StudioRevision;
      nodeId?: string;
    }
  | { kind: "document"; documentId: string }
  | { kind: "document-page"; documentId: string; pageId: string }
  | { kind: "document-node"; documentId: string; nodeId: string }
  | { kind: "document-proposal"; documentId: string; proposalId: string }
  | { kind: "report"; reportId: string }
  | { kind: "report-section"; reportId: string; sectionId: string }
  | { kind: "export-target"; target: StudioDocumentTarget; artifactId?: string }
  | { kind: "release"; projectId: string };

export type StudioTargetKind = StudioTargetRef["kind"];

export interface StudioContextFrame {
  projectId?: string;
  activeSurface: RailItemId;
  target?: StudioTargetRef;
  revision?: StudioRevision;
  sourceRevision?: StudioRevision;
}

export interface StudioJsonSchema {
  type?:
    | "object"
    | "array"
    | "string"
    | "number"
    | "integer"
    | "boolean"
    | "null";
  description?: string;
  properties?: Record<string, StudioJsonSchema>;
  required?: readonly string[];
  items?: StudioJsonSchema;
  enum?: readonly unknown[];
  const?: unknown;
  additionalProperties?: boolean | StudioJsonSchema;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  minItems?: number;
  minProperties?: number;
  anyOf?: readonly StudioJsonSchema[];
}

export type StudioOperationRisk =
  | "read"
  | "reversible"
  | "destructive"
  | "external";
export type StudioOperationConfirmation = "none" | "user" | "typed";
export type StudioOperationAvailability = "ready" | "proposal-only" | "blocked";
export type StudioDirectUiStatus =
  | "available"
  | "partial"
  | "chat-only"
  | "developer-only";

export interface StudioOperationPrecondition {
  kind:
    | "project-loaded"
    | "target-resolved"
    | "source-gates-passed"
    | "validation-passed"
    | "user-confirmed";
  description: string;
}

export interface StudioOperationExecutor {
  transport: "webmcp" | "webmcp-studio-bridge";
  name: string;
}

export interface StudioOperationReadback {
  mode: "tool-result" | "surface-state" | "validation-proof" | "artifact-proof";
  description: string;
  toolName?: string;
}

export interface StudioOperationUndo {
  mode: "none" | "latest" | "transaction" | "compensating";
  description: string;
}

export interface StudioOperationSurface {
  moduleId: StudioModuleId;
  railItemId: RailItemId;
  directUi: StudioDirectUiStatus;
  developerMode: boolean;
}

export interface StudioOperationLegacyFields {
  id: string;
  label: string;
  kind: string;
  surfaceId?: string;
  status: "ready" | "partial" | "gap";
  webMcpToolName?: string;
  studioMcpToolName?: string;
  requiredWebMcpToolName?: string;
  readOnly?: boolean;
  requiresConfirmation?: boolean;
  description: string;
  inputHint?: Record<string, unknown>;
  guidance?: string;
}

export interface StudioOperationV2Profile {
  moduleId: StudioModuleId;
  targetKinds: readonly StudioTargetKind[];
  inputSchema: StudioJsonSchema & { type: "object" };
  risk: StudioOperationRisk;
  confirmation?: StudioOperationConfirmation;
  preconditions?: readonly StudioOperationPrecondition[];
  executeTool?: StudioOperationExecutor;
  readback?: StudioOperationReadback;
  undo?: StudioOperationUndo;
  directUi: StudioDirectUiStatus;
  developerMode?: boolean;
  examples?: readonly string[];
  availability?: StudioOperationAvailability;
}

export interface StudioOperationV2 extends StudioOperationLegacyFields {
  contractVersion: "2.0";
  userLabel: string;
  moduleId: StudioModuleId;
  targetKinds: readonly StudioTargetKind[];
  inputSchema: StudioJsonSchema & { type: "object" };
  preconditions: readonly StudioOperationPrecondition[];
  risk: StudioOperationRisk;
  confirmation: StudioOperationConfirmation;
  executeTool: StudioOperationExecutor;
  readback: StudioOperationReadback;
  undo: StudioOperationUndo;
  surface: StudioOperationSurface;
  examples: readonly string[];
  availability: StudioOperationAvailability;
  readOnly: boolean;
  requiresConfirmation: boolean;
}

function defaultReadback(risk: StudioOperationRisk): StudioOperationReadback {
  if (risk === "read") {
    return {
      mode: "tool-result",
      description: "Return bounded tool output without mutating Studio state.",
    };
  }
  if (risk === "external") {
    return {
      mode: "artifact-proof",
      description:
        "Retain external artifact or release proof before claiming completion.",
    };
  }
  return {
    mode: "surface-state",
    description:
      "Refresh the owning Studio surface and return the resulting revision.",
  };
}

function defaultUndo(risk: StudioOperationRisk): StudioOperationUndo {
  if (risk === "reversible") {
    return {
      mode: "latest",
      description: "Undo the latest confirmed Studio mutation.",
    };
  }
  if (risk === "destructive") {
    return {
      mode: "transaction",
      description: "Restore the retained pre-operation transaction snapshot.",
    };
  }
  return {
    mode: "none",
    description:
      risk === "external"
        ? "External effects require a separate compensating operation."
        : "Read-only operations do not require undo.",
  };
}

export function buildStudioOperationV2(
  legacy: StudioOperationLegacyFields,
  profile: StudioOperationV2Profile,
): StudioOperationV2 {
  const toolName =
    profile.executeTool?.name ??
    legacy.webMcpToolName ??
    legacy.studioMcpToolName ??
    legacy.requiredWebMcpToolName;
  if (!toolName) {
    throw new Error(`Studio operation "${legacy.id}" has no executable tool.`);
  }

  const confirmation =
    profile.confirmation ??
    (legacy.requiresConfirmation === true ? "user" : "none");
  const moduleContract = STUDIO_MODULE_CONTRACTS[profile.moduleId];
  const preconditions: StudioOperationPrecondition[] = [
    {
      kind: "project-loaded",
      description: "A Studio project or source workspace must be active.",
    },
    ...(profile.targetKinds.length > 0
      ? [
          {
            kind: "target-resolved" as const,
            description: `Resolve one of: ${profile.targetKinds.join(", ")}.`,
          },
        ]
      : []),
    ...(confirmation === "none"
      ? []
      : [
          {
            kind: "user-confirmed" as const,
            description:
              confirmation === "typed"
                ? "Require typed confirmation for this high-impact operation."
                : "Require explicit user confirmation before execution.",
          },
        ]),
    ...(profile.preconditions ?? []),
  ];

  return {
    ...legacy,
    contractVersion: "2.0",
    userLabel: legacy.label,
    moduleId: profile.moduleId,
    targetKinds: [...profile.targetKinds],
    inputSchema: profile.inputSchema,
    preconditions,
    risk: profile.risk,
    confirmation,
    executeTool:
      profile.executeTool ??
      ({
        transport: toolName.startsWith("cogo.studio.")
          ? "webmcp-studio-bridge"
          : "webmcp",
        name: toolName,
      } satisfies StudioOperationExecutor),
    readback: profile.readback ?? defaultReadback(profile.risk),
    undo: profile.undo ?? defaultUndo(profile.risk),
    surface: {
      moduleId: profile.moduleId,
      railItemId: moduleContract.primarySurface,
      directUi: profile.directUi,
      developerMode: profile.developerMode ?? false,
    },
    examples: profile.examples ?? [legacy.description],
    availability:
      profile.availability ??
      (legacy.status === "ready"
        ? "ready"
        : legacy.status === "partial"
          ? "proposal-only"
          : "blocked"),
    readOnly: profile.risk === "read",
    requiresConfirmation: confirmation !== "none",
  };
}

export interface StudioOperationToolManifestEntry {
  name: string;
  inputSchema?: StudioJsonSchema;
  metadata?: { readOnly?: boolean };
}

export interface StudioOperationParityIssue {
  operationId: string;
  severity: "error" | "warning";
  code:
    | "duplicate-id"
    | "missing-target"
    | "invalid-input-schema"
    | "missing-tool"
    | "tool-schema-mismatch"
    | "read-only-mismatch"
    | "missing-confirmation"
    | "unsafe-destructive-confirmation"
    | "surface-mismatch"
    | "missing-example";
  message: string;
}

export function validateStudioOperationParity(
  operations: readonly StudioOperationV2[],
  toolManifest: readonly StudioOperationToolManifestEntry[],
): StudioOperationParityIssue[] {
  const issues: StudioOperationParityIssue[] = [];
  const seen = new Set<string>();
  const tools = new Map(toolManifest.map((entry) => [entry.name, entry]));

  for (const operation of operations) {
    if (seen.has(operation.id)) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "duplicate-id",
        message: `Operation id "${operation.id}" is registered more than once.`,
      });
    }
    seen.add(operation.id);

    if (operation.targetKinds.length === 0) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "missing-target",
        message: "Operation must declare at least one target kind.",
      });
    }
    if (
      operation.inputSchema.type !== "object" ||
      operation.inputSchema.additionalProperties !== false
    ) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "invalid-input-schema",
        message:
          "Operation input must use a closed top-level object JSON Schema.",
      });
    }

    const tool = tools.get(operation.executeTool.name);
    const usesOperationDispatcher =
      operation.executeTool.name === STUDIO_CAPABILITY_OPERATION_DISPATCHER;
    if (!tool) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "missing-tool",
        message: `Tool "${operation.executeTool.name}" is absent from the WebMCP manifest.`,
      });
    } else if (
      !usesOperationDispatcher &&
      typeof tool.metadata?.readOnly === "boolean" &&
      tool.metadata.readOnly !== operation.readOnly
    ) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "read-only-mismatch",
        message: `Tool readOnly=${String(tool.metadata.readOnly)} conflicts with operation risk=${operation.risk}.`,
      });
    } else if (
      !usesOperationDispatcher &&
      operation.readOnly &&
      tool.metadata?.readOnly !== true
    ) {
      issues.push({
        operationId: operation.id,
        severity: "warning",
        code: "read-only-mismatch",
        message:
          "Read-only operation tool should explicitly declare readOnly=true.",
      });
    }
    if (usesOperationDispatcher && tool?.inputSchema?.type === "object") {
      const toolProperties = tool.inputSchema.properties ?? {};
      const toolRequired = new Set(tool.inputSchema.required ?? []);
      // The dispatcher only carries an operation id plus the operation's own
      // bounded parameters. Its read-only metadata describes planning, not the
      // confirmed mutation that the operation contract describes.
      if (
        !toolRequired.has("operationId") ||
        !("params" in toolProperties)
      ) {
        issues.push({
          operationId: operation.id,
          severity: "error",
          code: "tool-schema-mismatch",
          message:
            "The Studio operation dispatcher must require operationId and accept params.",
        });
      }
    } else if (tool?.inputSchema?.type === "object") {
      const operationProperties = operation.inputSchema.properties ?? {};
      const toolProperties = tool.inputSchema.properties ?? {};
      const operationRequired = new Set(operation.inputSchema.required ?? []);
      const toolRequired = new Set(tool.inputSchema.required ?? []);
      for (const property of operationRequired) {
        if (!(property in toolProperties)) {
          issues.push({
            operationId: operation.id,
            severity: "error",
            code: "tool-schema-mismatch",
            message:
              "Required operation input " +
              JSON.stringify(property) +
              " is absent from tool " +
              JSON.stringify(operation.executeTool.name) +
              ".",
          });
        }
      }
      for (const property of toolRequired) {
        if (!operationRequired.has(property)) {
          issues.push({
            operationId: operation.id,
            severity: "error",
            code: "tool-schema-mismatch",
            message:
              "Tool-required input " +
              JSON.stringify(property) +
              " is not required by the operation contract.",
          });
        }
      }
      for (const [property, operationProperty] of Object.entries(
        operationProperties,
      )) {
        const toolProperty = toolProperties[property];
        if (
          toolProperty?.type &&
          operationProperty.type &&
          toolProperty.type !== operationProperty.type
        ) {
          issues.push({
            operationId: operation.id,
            severity: "error",
            code: "tool-schema-mismatch",
            message:
              "Input " +
              JSON.stringify(property) +
              " type " +
              operationProperty.type +
              " conflicts with tool type " +
              toolProperty.type +
              ".",
          });
        }
      }
    }

    if (!operation.readOnly && operation.confirmation === "none") {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "missing-confirmation",
        message: "Mutating and external operations require confirmation.",
      });
    }
    if (
      operation.risk === "destructive" &&
      operation.confirmation !== "typed"
    ) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "unsafe-destructive-confirmation",
        message: "Destructive operations require typed confirmation.",
      });
    }
    if (
      operation.surface.railItemId !==
      STUDIO_MODULE_CONTRACTS[operation.moduleId].primarySurface
    ) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "surface-mismatch",
        message: "Operation surface does not match its owning Studio module.",
      });
    }
    if (operation.examples.length === 0) {
      issues.push({
        operationId: operation.id,
        severity: "error",
        code: "missing-example",
        message: "Operation must include a natural-language example.",
      });
    }
  }

  return issues;
}
